import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/**
 * SignaturePad — touch + mouse signature canvas (pointer events, DPR-aware).
 * ref API: clear(), isEmpty(), toDataURL()
 */
export const SignaturePad = forwardRef(({ onChange, testId = "signature-pad" }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const emptyRef = useRef(true);
  const [empty, setEmpty] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0B1220";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x + 0.1, p.y + 0.1);
    ctx.stroke();
    if (emptyRef.current) { emptyRef.current = false; setEmpty(false); onChange?.(false); }
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    emptyRef.current = true;
    setEmpty(true);
    onChange?.(true);
  };

  useImperativeHandle(ref, () => ({
    clear,
    isEmpty: () => emptyRef.current,
    toDataURL: () => canvasRef.current?.toDataURL("image/png"),
  }));

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
        className="w-full h-36 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 cursor-crosshair"
        style={{ touchAction: "none" }}
        data-testid={testId}
      />
      {empty && (
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <span className="text-[12.5px] text-slate-400 italic">Sign here with your finger or mouse</span>
        </div>
      )}
      {!empty && (
        <button type="button" onClick={clear}
                className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 h-6 text-[10.5px] font-semibold text-slate-500 hover:text-slate-800 shadow-sm"
                data-testid={`${testId}-clear`}>
          <Eraser className="size-3" /> Clear
        </button>
      )}
    </div>
  );
});
SignaturePad.displayName = "SignaturePad";
